import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ArrowDownTrayIcon, ArrowUpTrayIcon, DocumentTextIcon, EyeIcon, FolderIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/20/solid";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import Select from "../forms/Select/Select";
import { Button } from "../forms/Buttons/Button";
import PreviewQuestion, {
  NOT_MARKED,
  NO_CORRECT,
  REQUIRED_FIELDS,
  collectDocxRefs,
  contentMissing,
  docxRefsFromImages,
  editProblem,
  hasWarning,
  lacksOptions,
  toEditEntry,
} from "./ImportPreviewQuestion";
import { SubtopicFields, useGradeOptions, useSubtopicCatalog } from "./ImportSubtopicPicker";
import { DIFFICULTY_POINTS, DifficultyPicker } from "../Difficulty/Difficulty";
import { getQuestionFolders, uploadFileWithQuestions } from "../../services/questionService";
import { canCreateIn, subjectFolderOptions } from "../../utils/folders";
import { getApiErrorCode, getApiErrorMessage } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";

// Fields of the form step, applied to every question in the file (contract section 8). The subtopic is
// chosen in the preview: a default for the whole file (`subtopic`) and one per question (`subtopics`).
// `grade` is the form's own: it narrows the preview's topics to that grade's (nothing is sent for it).
const EMPTY_FIELDS = { folder: "", subject: "", grade: "" };
// The production nginx limit for one request (client_max_body_size).
const MAX_UPLOAD_MB = 20;
// Question numbers listed for a missing field in the summary before "N more".
const MAX_LISTED = 12;

const toList = (data) => (Array.isArray(data) ? data : data?.results || []);

// The object without `key` (the same object when it has none).
const omit = (obj, key) => {
  if (!obj || !Object.prototype.hasOwnProperty.call(obj, key)) return obj;
  const { [key]: omitted, ...rest } = obj;
  return rest;
};
// A field name from question_fields ("subtopic") at the start of a summary line ("Subtopic").
const capitalize = (text) => (text ? text.charAt(0).toLocaleUpperCase() + text.slice(1) : "");

// A question changed after a refused save loses the server's marks: the checks here run on it again.
const unmark = (p, index) => (p.serverMissing[index] ? { ...p, serverMissing: omit(p.serverMissing, index) } : p);

// Another subject: the subtopics picked for the old one do not belong to it.
const withSubject = (p, subjectUuid) => ({
  ...p,
  subjectUuid,
  subtopics: {},
  generalSubtopic: null,
  subtopicErrors: {},
  serverMissing: {},
});

// A dry-run made again for another folder (same file, same numbers) keeps what was done in the preview;
// the subtopics only within the same subject.
const carryOver = (fresh, old) => ({
  ...fresh,
  edits: old.edits,
  removed: old.removed,
  difficulty: fresh.acceptsDifficulty ? old.difficulty : {},
  ...(old.subjectUuid === fresh.subjectUuid ? { subtopics: old.subtopics, generalSubtopic: old.generalSubtopic } : {}),
});

// The instructions are quiet links: the file and "Preview" are what the form is for.
const linkClass =
  "inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 md:min-h-[2.25rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
// The summary's question numbers; phones give each a 44px touch target.
const jumpClass =
  "rounded px-0.5 font-semibold tabular-nums underline decoration-dotted underline-offset-2 transition duration-200 hover:decoration-solid max-md:inline-flex max-md:min-h-[2.75rem] max-md:min-w-[2.75rem] max-md:items-center max-md:justify-center focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-blue-600";

/**
 * docx import dialog shared by My Questions and the Question Bank, in two steps: the file is first
 * parsed with dry_run (nothing is saved, the questions are shown), then the same file and fields are
 * sent again to save them. When the dry-run says `accepts_edits`, the preview also lets the user pick
 * correct answers and edit questions; the changed questions are sent along as `edits` (JSON).
 * - `folder` (an exam's subject) fixes the target folder (the picker is replaced by `folderLabel`, e.g.
 *   "ТАТ · Biology"); without it the user picks an exam's subject where their role writes questions (the
 *   picker is hidden when the folders endpoint fails). The questions take the folder's subject.
 * - `allowNoFolder`: importing without a folder (then with a subject of its own) needs the global
 *   create_question; without it a folder is required (the server answers 403 folder_required).
 * - `defaultSubject` pre-selects the subject of an import without a folder.
 * - `onUploaded(count)` runs after a successful import; `onFolderDeleted()` when the fixed folder
 *   was deleted meanwhile (409 folder_deleted) — the dialog closes itself in that case.
 * With `accepts_subtopics`, the preview picks subtopics of the target subject: a default one and each
 * question's own. Into a subject (`required`: subtopic and difficulty) only complete questions are saved:
 * the preview lists what each one lacks and saving waits until nothing does (the server checks it again:
 * 400 incomplete / invalid_subtopics mark the questions it names).
 */
export default function UploadQuestionsDialog({
  isOpen,
  onClose,
  folder = null,
  folderLabel = "",
  allowNoFolder = true,
  defaultSubject = "",
  subjects = [],
  onUploaded,
  onFolderDeleted,
}) {
  const [t] = useTranslation();
  const fileInputRef = useRef(null);
  const previewAbortRef = useRef(null);
  const listId = useId();
  const generalErrorId = useId();
  const gradeInputId = useId();
  // The opening the state was last reset for (see below), or null while closed.
  const [resetFor, setResetFor] = useState(null);
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [file, setFile] = useState(null);
  // "form" (file + fields) or "preview" (the parsed questions, waiting for confirmation).
  const [step, setStep] = useState("form");
  // The dry-run result for the current file and folder (cleared with a new file; another folder runs it
  // again and keeps the work done in it):
  // { count, questions, acceptsEdits, edits: { [index]: edited question }, images: { DOCX_n: data URI } | null,
  //   acceptsSkip, removed: { [index]: true }, acceptsDifficulty, difficulty: { [index]: "A" | "B" | "C" },
  //   acceptsSubtopics, subjectUuid (the target's subject), generalSubtopic: subtopic | null,
  //   subtopics: { [index]: subtopic }, required: ["subtopic", "difficulty"] into a subject, else [],
  //   forFolder (the folder it was made for), serverMissing: { [index]: [field] } and
  //   subtopicErrors: { [index]: true } from a refused save }
  // — removed questions are left out of the import (sent as `skip`), picked difficulties go as `difficulty`,
  // the default subtopic as `subtopic`, each question's own as `subtopics` ({subtopic: uuid, name, topic}).
  const [preview, setPreview] = useState(null);
  // Index of the question open in the inline editor (one at a time), or null.
  const [editingIndex, setEditingIndex] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [saving, setSaving] = useState(false);
  // null = the folders endpoint is unavailable (old backend / error): the folder field is hidden.
  const [folders, setFolders] = useState(null);
  // Depend on the uuid, not the object: callers may pass a new {uuid, name} object on every render.
  const folderUuid = folder?.uuid || "";
  const busy = previewing || saving;

  // Every opening starts from scratch (the next file must not silently reuse the last folder/subject).
  // Reset while the opening renders: an effect would first render the previous preview list again
  // (KaTeX for every question), and a reset on close would swap the content while the panel fades out.
  const openedWith = isOpen ? `${folderUuid}|${defaultSubject || ""}` : null;
  if (openedWith !== resetFor) {
    setResetFor(openedWith);
    if (openedWith !== null) {
      setFields({ ...EMPTY_FIELDS, folder: folderUuid, subject: defaultSubject || "" });
      setFieldErrors({});
      setError("");
      setFile(null);
      setStep("form");
      setPreview(null);
      setEditingIndex(null);
    }
  }

  // Closing (or leaving the page) cancels a dry-run still in progress; it saves nothing.
  useEffect(() => {
    if (!isOpen) return undefined;
    return () => previewAbortRef.current?.abort();
  }, [isOpen]);

  const loadFolders = useCallback((signal) => {
    getQuestionFolders({ signal })
      .then((data) => {
        const list = toList(data);
        setFolders(list);
        // A folder picked earlier may have been deleted since: drop it rather than send a stale uuid.
        setFields((prev) => (prev.folder && !list.some((f) => f.uuid === prev.folder) ? { ...prev, folder: "" } : prev));
      })
      .catch((err) => {
        if (isCanceledError(err)) return;
        setFolders(null);
        setFields((prev) => (prev.folder ? { ...prev, folder: "" } : prev));
      });
  }, []);

  useEffect(() => {
    if (!isOpen || folderUuid) return undefined;
    const controller = new AbortController();
    loadFolders(controller.signal);
    return () => controller.abort();
  }, [isOpen, folderUuid, loadFolders]);

  // The subject the questions go to: the folder's, or the one picked for an import without a folder.
  const targetFolder = folder || (folders || []).find((f) => f.uuid === fields.folder) || null;
  const subjectUuid = fields.folder ? targetFolder?.subject?.uuid || "" : fields.subject || "";
  const showSubject = !folder && !fields.folder && allowNoFolder;
  // The subject's topics and subtopics, shared by the preview's pickers (and the grades of the form's field).
  const catalog = useSubtopicCatalog(
    step === "preview" && preview ? (preview.acceptsSubtopics ? preview.subjectUuid : "") : subjectUuid
  );
  const gradeOptions = useGradeOptions(catalog);
  const grade = fields.grade ? Number(fields.grade) : null;

  // Another folder or subject has other grades: the grade is chosen again.
  const updateField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value, ...(key === "folder" || key === "subject" ? { grade: "" } : {}) }));
    setFieldErrors((prev) => (prev[key] ? { ...prev, [key]: "" } : prev));
    setError("");
  };

  // Same rule as the server: without global rights the questions go into a subject.
  const validate = () => {
    if (!fields.folder && !allowNoFolder) {
      setFieldErrors({ folder: t("errors.folder_required") });
      return false;
    }
    return true;
  };

  const inPreview = step === "preview" && !!preview;
  const removedCount = preview ? Object.keys(preview.removed).length : 0;
  // How many questions the import creates: the file's minus the ones removed in the preview.
  const saveCount = preview ? Math.max(0, preview.count - removedCount) : 0;
  // Unchanged questions keep their identity here, so the memoized list items do not re-render.
  const shownQuestions = inPreview ? preview.questions.map((q) => preview.edits[q.index] || q) : [];
  // Removed questions are not saved, so their warnings no longer matter.
  const keptQuestions = inPreview ? shownQuestions.filter((q) => !preview.removed[q.index]) : [];

  // Into a subject only complete questions are saved: what each kept one still lacks, in the server's
  // order (its own subtopic or the default one; difficulty; content), plus the server's marks from a
  // refused save until the question changes. index -> { missing: [field], invalid, rejected }.
  const strict = inPreview && preview.required.length > 0;
  const needSubtopic = strict && preview.required.includes("subtopic");
  const needDifficulty = strict && preview.required.includes("difficulty");
  const checks = new Map();
  keptQuestions.forEach((q) => {
    const found = new Set(strict ? contentMissing(q) : []);
    if (needSubtopic && !preview.subtopics[q.index] && !preview.generalSubtopic) found.add("subtopic");
    if (needDifficulty && !preview.difficulty[q.index]) found.add("difficulty");
    (preview.serverMissing[q.index] || []).forEach((field) => found.add(field));
    const missing = [
      ...REQUIRED_FIELDS.filter((field) => found.has(field)),
      ...[...found].filter((field) => !REQUIRED_FIELDS.includes(field)),
    ];
    const invalid = !!preview.subtopicErrors[q.index];
    if (missing.length || invalid) {
      checks.set(q.index, { missing, invalid, rejected: invalid || !!preview.serverMissing[q.index] });
    }
  });
  const problems = keptQuestions.filter((q) => checks.has(q.index));
  const blocked = problems.length > 0;
  const anyRejected = problems.some((q) => checks.get(q.index).rejected);
  // Per field, the questions that lack it (a refused subtopic counts as one to choose again).
  const fieldsOf = (q) => {
    const check = checks.get(q.index);
    return check.invalid && !check.missing.includes("subtopic") ? ["subtopic", ...check.missing] : check.missing;
  };
  const summaryFields = [...new Set([...REQUIRED_FIELDS, ...problems.flatMap(fieldsOf)])]
    .map((field) => ({ field, indexes: problems.filter((q) => fieldsOf(q).includes(field)).map((q) => q.index) }))
    .filter((row) => row.indexes.length > 0);

  const hasEdits =
    !!preview &&
    (editingIndex !== null ||
      Object.keys(preview.edits).length > 0 ||
      removedCount > 0 ||
      Object.keys(preview.difficulty).length > 0 ||
      Object.keys(preview.subtopics || {}).length > 0 ||
      !!preview.generalSubtopic);
  const close = () => {
    // A dry-run is canceled by closing, but the import may already be committing while it is saved.
    if (saving) return;
    // Esc, a click outside or a toast click would silently drop the picked answers and edits.
    if (hasEdits && !window.confirm(t("my_questions.upload.discard_confirm"))) return;
    onClose();
  };

  // In a folder the subject is the folder's; a subject of its own goes only with an import without one.
  const payload = () => ({
    folder: fields.folder,
    subject: fields.folder ? "" : fields.subject,
  });

  const finishImport = (count) => {
    toast.success(t("my_questions.upload.created", { count }));
    onClose();
    onUploaded?.(count);
  };

  // Brings a question of the list into view and focus (from the summary, or after a refused save).
  const questionDomId = (index) => `${listId}-q${index}`;
  const jumpToQuestion = (index) => {
    window.requestAnimationFrame(() => {
      const el = document.getElementById(questionDomId(index));
      if (!el) return;
      el.scrollIntoView({ block: "center", behavior: "smooth" });
      el.focus({ preventScroll: true });
    });
  };

  const handleError = (err) => {
    if (!err?.response) {
      // No answer: offline, or the file changed on disk after it was chosen (the browser refuses to send it).
      setError(t("my_questions.upload.send_failed"));
      return;
    }
    const data = err.response.data;
    const code = getApiErrorCode(err);
    if (code === "invalid_edits") {
      // The server names the question it refused: open it so it can be fixed.
      if (Number.isInteger(data.index)) {
        setError(t("my_questions.upload.invalid_edit_question", { index: data.index, detail: data.detail || "" }));
        setEditingIndex((current) => current ?? data.index);
      } else {
        setError(getApiErrorMessage(t, err, "my_questions.upload.failed"));
      }
      return;
    }
    if (code === "incomplete" && Array.isArray(data?.items)) {
      // Nothing was saved: mark the questions the server named (`index` is the preview's number).
      const marks = {};
      data.items.forEach((item) => {
        if (Number.isInteger(item?.index) && Array.isArray(item.missing)) marks[item.index] = item.missing;
      });
      setPreview((p) => (p ? { ...p, serverMissing: marks } : p));
      setError(t("my_questions.upload.incomplete.rejected", { n: Object.keys(marks).length || data.items.length }));
      const first = Object.keys(marks).map(Number).sort((a, b) => a - b)[0];
      if (first) jumpToQuestion(first);
      return;
    }
    if (code === "invalid_subtopics") {
      // A question's own subtopic is not of the target subject (or is gone): choose it again.
      if (Number.isInteger(data?.index)) {
        setPreview((p) => (p ? { ...p, subtopicErrors: { ...p.subtopicErrors, [data.index]: true } } : p));
        setError(t("my_questions.upload.incomplete.invalid_subtopic", { index: data.index }));
        jumpToQuestion(data.index);
      } else {
        setError(getApiErrorMessage(t, err, "my_questions.upload.failed"));
      }
      return;
    }
    // A fixed folder that no longer exists (409, or a 400 on the folder field): nothing left to do here.
    if (folder && (code === "folder_deleted" || (!code && data?.folder))) {
      toast.error(code ? getApiErrorMessage(t, err, "my_questions.upload.failed") : [].concat(data.folder).join(" "));
      onClose();
      onFolderDeleted?.();
      return;
    }
    if (code === "folder_deleted") {
      // Pick another folder; the preview and its edits stay.
      setFields((prev) => ({ ...prev, folder: "" }));
      setStep("form");
      loadFolders();
    }
    // Field errors ({subject: [...]}) go under their fields where they are shown (the default subtopic's
    // under its picker in the preview); anything else is shown in the alert.
    if (!code && data && typeof data === "object" && !Array.isArray(data) && !data.detail) {
      const nextFieldErrors = {};
      const otherMessages = [];
      Object.entries(data).forEach(([key, value]) => {
        const message = [].concat(value).filter((v) => typeof v === "string").join(" ");
        if (!message) return;
        const shown = key === "folder" || (key === "subject" && showSubject) || (key === "subtopic" && inPreview);
        if (shown) nextFieldErrors[key] = message;
        else otherMessages.push(message);
      });
      if (Object.keys(nextFieldErrors).length || otherMessages.length) {
        setFieldErrors(nextFieldErrors);
        setError(otherMessages.join(" "));
        if (nextFieldErrors.folder || nextFieldErrors.subject) setStep("form");
        return;
      }
    }
    setError(getApiErrorMessage(t, err, "my_questions.upload.failed"));
  };

  const handleFileChange = (e) => {
    const chosen = e.target.files[0];
    // Reset the input so choosing the same file again still fires onChange.
    e.target.value = "";
    if (!chosen) return;
    // The production proxy refuses larger requests with an HTML page, which would read as a generic failure.
    if (chosen.size > MAX_UPLOAD_MB * 1024 * 1024) {
      setError(t("my_questions.upload.too_large", { mb: MAX_UPLOAD_MB }));
      return;
    }
    setFile(chosen);
    setPreview(null);
    setEditingIndex(null);
    setError("");
  };

  const handlePreview = async () => {
    if (!file || busy || !validate()) return;
    // The same folder: the preview stands (another subject drops the subtopics picked for the old one).
    if (preview && preview.forFolder === fields.folder) {
      if (preview.subjectUuid !== subjectUuid) setPreview((p) => withSubject(p, subjectUuid));
      setStep("preview");
      return;
    }
    // Another folder changes what is required, so the file is read again; the work done in the preview stays.
    const carried = preview;
    setPreviewing(true);
    setError("");
    setFieldErrors({});
    const controller = new AbortController();
    previewAbortRef.current = controller;
    try {
      // inline_images=false: each docx image comes once, in `images`, instead of inside every HTML that
      // shows it (a backend without the flag ignores it and inlines them).
      const data = await uploadFileWithQuestions(
        file,
        { ...payload(), dry_run: "true", inline_images: "false" },
        controller.signal
      );
      if (Array.isArray(data)) {
        // A backend without dry_run imports right away and returns the created questions: done.
        finishImport(data.length);
        return;
      }
      // Edits are keyed by the dry-run index: make sure every question has one. defaultName is the
      // name the file gives (an emptied name in the editor falls back to it).
      const questions = (Array.isArray(data?.questions) ? data.questions : []).map((q, i) => ({
        ...q,
        index: q.index ?? i + 1,
        defaultName: q.name || "",
      }));
      const fresh = {
        count: data?.count ?? questions.length,
        questions,
        acceptsEdits: data?.accepts_edits === true,
        edits: {},
        images: data?.images && typeof data.images === "object" ? data.images : null,
        // Only a backend that says so leaves questions out: an older one would import them anyway.
        acceptsSkip: data?.accepts_skip === true,
        removed: {},
        acceptsDifficulty: data?.accepts_difficulty === true,
        difficulty: {},
        acceptsSubtopics: data?.accepts_subtopics === true,
        subjectUuid,
        generalSubtopic: null,
        subtopics: {},
        required: Array.isArray(data?.required) ? data.required : [],
        forFolder: fields.folder,
        serverMissing: {},
        subtopicErrors: {},
      };
      setPreview(carried ? carryOver(fresh, carried) : fresh);
      setEditingIndex(null);
      setStep("preview");
    } catch (err) {
      // Canceled by closing the dialog: nothing to show.
      if (!isCanceledError(err)) handleError(err);
    } finally {
      setPreviewing(false);
    }
  };

  // Clicking an option makes it the only correct one (confirming an assumed answer clears its warning).
  // `question` is the version on screen (with earlier edits).
  const pickCorrect = useCallback((question, option) => {
    const keys = question.answer_keys || [];
    const assumed = hasWarning(question, NOT_MARKED) || hasWarning(question, NO_CORRECT);
    if (!assumed && keys.every((o) => !!o.is_correct === (o.option === option))) return;
    const edited = {
      ...question,
      answer_keys: keys.map((o) => ({ ...o, is_correct: o.option === option })),
      warnings: (question.warnings || []).filter((w) => w !== NOT_MARKED && w !== NO_CORRECT),
      edited: true,
    };
    // Every edited question is validated by the server: one it would refuse (e.g. a single option)
    // would block the whole import, so it goes to the editor instead.
    if (editProblem(edited)) {
      setEditingIndex((current) => current ?? question.index);
      return;
    }
    setError("");
    setPreview((p) => unmark({ ...p, edits: { ...p.edits, [question.index]: edited } }, question.index));
  }, []);

  const startEdit = useCallback((index) => setEditingIndex((current) => current ?? index), []);
  const cancelEdit = useCallback(() => setEditingIndex(null), []);
  const applyEdit = useCallback((question) => {
    setPreview((p) => unmark({ ...p, edits: { ...p.edits, [question.index]: question } }, question.index));
    setEditingIndex(null);
    setError("");
  }, []);
  // Back to the version from the file.
  const revertEdit = useCallback((index) => {
    setPreview((p) => unmark({ ...p, edits: omit(p.edits, index) }, index));
  }, []);
  // A question parsed wrong is left out of the import; until saving it can be restored (with its edit).
  const removeQuestion = useCallback((index) => {
    setPreview((p) => ({
      ...unmark(p, index),
      removed: { ...p.removed, [index]: true },
      subtopicErrors: omit(p.subtopicErrors, index),
    }));
    setError("");
  }, []);
  const restoreQuestion = useCallback((index) => {
    setPreview((p) => ({ ...p, removed: omit(p.removed, index) }));
  }, []);
  // A level, or null to leave the question without difficulty.
  const pickDifficulty = useCallback((index, level) => {
    setPreview((p) => {
      const difficulty = omit(p.difficulty, index);
      return unmark({ ...p, difficulty: level ? { ...difficulty, [index]: level } : difficulty }, index);
    });
  }, []);
  // The same level for every question that will be saved (null clears them all).
  const pickDifficultyForAll = (level) => {
    setPreview((p) => ({
      ...p,
      difficulty: level
        ? Object.fromEntries(p.questions.filter((q) => !p.removed[q.index]).map((q) => [q.index, level]))
        : {},
      serverMissing: {},
    }));
  };
  // A question's own subtopic, or null to take the default one.
  const pickSubtopic = useCallback((index, subtopic) => {
    setPreview((p) => ({
      ...unmark(p, index),
      subtopics: subtopic ? { ...p.subtopics, [index]: subtopic } : omit(p.subtopics, index),
      subtopicErrors: omit(p.subtopicErrors, index),
    }));
  }, []);
  // The default subtopic: every question without its own takes it.
  const pickGeneralSubtopic = useCallback((subtopic) => {
    setPreview((p) => ({ ...p, generalSubtopic: subtopic, serverMissing: {} }));
    setFieldErrors((prev) => (prev.subtopic ? { ...prev, subtopic: "" } : prev));
  }, []);

  // data URI -> DOCX_n of the parsed images (the editor restores references on copied images).
  // A backend without inline_images=false has the data URIs in the HTML instead.
  const refByDataUri = useMemo(() => {
    const map = docxRefsFromImages(preview?.images, new Map());
    (preview?.questions || []).forEach((q) =>
      [q.body, ...(q.answer_keys || []).map((o) => o.body)].forEach((html) => collectDocxRefs(html, map))
    );
    return map;
  }, [preview?.images, preview?.questions]);

  // Confirm: the same File and fields again, without dry_run (parsing is deterministic), plus the
  // questions changed in the preview.
  const handleSave = async () => {
    if (!file || busy || editingIndex !== null || !saveCount || blocked) return;
    setSaving(true);
    setError("");
    const removed = preview?.removed || {};
    // A removed question's edit is not sent: the server would validate it for nothing.
    const edits = Object.values(preview?.edits || {})
      .filter((q) => !removed[q.index])
      .map(toEditEntry);
    const skip = Object.keys(removed)
      .map(Number)
      .sort((a, b) => a - b);
    // {"<index>": "A" | "B" | "C"} for the questions given one; a removed question's is not sent.
    const difficulty = preview?.acceptsDifficulty
      ? Object.fromEntries(Object.entries(preview.difficulty).filter(([index, level]) => level && !removed[index]))
      : {};
    // {"<index>": "<subtopic uuid>"} for the questions given their own; the others take the default `subtopic`.
    const subtopics = preview?.acceptsSubtopics
      ? Object.fromEntries(
          Object.entries(preview.subtopics)
            .filter(([index, subtopic]) => subtopic?.uuid && !removed[index])
            .map(([index, subtopic]) => [index, subtopic.uuid])
        )
      : {};
    try {
      const data = await uploadFileWithQuestions(file, {
        ...payload(),
        subtopic: (preview?.acceptsSubtopics && preview.generalSubtopic?.uuid) || "",
        edits: edits.length ? JSON.stringify(edits) : "",
        skip: skip.length ? JSON.stringify(skip) : "",
        difficulty: Object.keys(difficulty).length ? JSON.stringify(difficulty) : "",
        subtopics: Object.keys(subtopics).length ? JSON.stringify(subtopics) : "",
      });
      finishImport(Array.isArray(data) ? data.length : saveCount);
    } catch (err) {
      handleError(err);
    } finally {
      setSaving(false);
    }
  };

  const notSet = { value: "", label: t("my_questions.upload.not_set") };
  const toOptions = (list) => [notSet, ...list.map((item) => ({ value: item.uuid, label: item.name }))];
  // Subjects grouped by exam, where the role writes questions; "No folder" only with global rights.
  const folderOptions = [
    ...(allowNoFolder ? [{ value: "", label: t("folders.no_folder") }] : []),
    ...subjectFolderOptions(folders || [], (f) => canCreateIn(f.role)),
  ];
  const showFolderPicker = !folder && folderOptions.length > (allowNoFolder ? 1 : 0);
  const notMarkedCount = keptQuestions.filter((q) => hasWarning(q, NOT_MARKED)).length;
  const noCorrectCount = keptQuestions.filter((q) => hasWarning(q, NO_CORRECT)).length;
  const noOptionsCount = keptQuestions.filter(lacksOptions).length;
  const difficultyCount = inPreview ? keptQuestions.filter((q) => preview.difficulty[q.index]).length : 0;
  // The points the picked difficulties give (questions without one get the test's default points later).
  const difficultyPoints = inPreview
    ? keptQuestions.reduce((sum, q) => sum + (DIFFICULTY_POINTS[preview.difficulty[q.index]] || 0), 0)
    : 0;
  // The level every saved question has, if they all have the same one.
  const keptLevels = inPreview ? new Set(keptQuestions.map((q) => preview.difficulty[q.index] || null)) : new Set();
  const commonDifficulty = keptLevels.size === 1 ? [...keptLevels][0] : null;
  const showSubtopics = inPreview && preview.acceptsSubtopics;
  const ownSubtopicCount = showSubtopics ? keptQuestions.filter((q) => preview.subtopics[q.index]).length : 0;
  const editing = editingIndex !== null;

  const errorAlert = error && (
    <div role="alert" className="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
      {error}
    </div>
  );

  return (
    <CustomDialog
      isOpen={isOpen}
      headerText={t("my_questions.upload.title")}
      closeModal={close}
      maxWidth={inPreview ? "max-w-4xl" : "max-w-2xl"}
    >
      {inPreview ? (
        <div className="w-full">
          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <p className="text-sm font-semibold text-slate-900">
              {t("my_questions.upload.preview_count", { count: preview.count })}
            </p>
            <p className="min-w-0 truncate text-xs text-slate-500">
              {file?.name}
              {folder ? ` · ${folderLabel || folder.name}` : ""}
            </p>
          </div>
          <p className="mb-3 text-sm text-slate-500">
            {preview.acceptsEdits ? t("my_questions.upload.preview_hint_editable") : t("my_questions.upload.preview_hint")}
            {preview.acceptsSkip && ` ${t("my_questions.upload.preview_hint_remove")}`}
          </p>

          {(showSubtopics || preview.acceptsDifficulty) && saveCount > 0 && (
            <div className="mb-3 divide-y divide-slate-200 rounded-xl border border-slate-200 bg-slate-50">
              {showSubtopics && (
                <div className="px-3 py-2.5">
                  <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                    <span className="text-sm font-medium text-slate-700">
                      {t("my_questions.upload.subtopics.general_label")}
                    </span>
                    {ownSubtopicCount > 0 && (
                      <span className="text-xs tabular-nums text-slate-500">
                        {t("my_questions.upload.subtopics.own_count", { n: ownSubtopicCount })}
                      </span>
                    )}
                  </div>
                  {catalog ? (
                    <>
                      {gradeOptions.length > 0 && (
                        <label className="mb-2 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-600">
                          {t("my_questions.upload.grade")}
                          <select
                            value={fields.grade}
                            onChange={(e) => updateField("grade", e.target.value)}
                            disabled={saving || editing}
                            className="h-9 rounded-lg border-slate-300 py-0 pl-3 pr-8 text-sm text-slate-900 shadow-sm focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 max-md:h-11"
                          >
                            <option value="">{t("my_questions.upload.grade_all")}</option>
                            {gradeOptions.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </label>
                      )}
                      <SubtopicFields
                        catalog={catalog}
                        value={preview.generalSubtopic}
                        onChange={pickGeneralSubtopic}
                        grade={grade}
                        disabled={saving || editing}
                        invalid={!!fieldErrors.subtopic}
                        errorId={fieldErrors.subtopic ? generalErrorId : undefined}
                      />
                      {fieldErrors.subtopic && (
                        <p id={generalErrorId} className="mt-1 text-xs font-semibold text-error">
                          {fieldErrors.subtopic}
                        </p>
                      )}
                      <p className="mt-1.5 text-xs text-slate-500">{t("my_questions.upload.subtopics.general_hint")}</p>
                    </>
                  ) : (
                    <p className="text-xs text-slate-500">{t("my_questions.upload.subtopics.no_subject")}</p>
                  )}
                </div>
              )}
              {preview.acceptsDifficulty && (
                <div className="px-3 py-2.5">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <span className="text-sm font-medium text-slate-700">{t("difficulty.set_all")}</span>
                    {/* Pressed when every saved question has that level; pressing it again clears them all. */}
                    <DifficultyPicker
                      value={difficultyCount === keptQuestions.length ? commonDifficulty : null}
                      onChange={pickDifficultyForAll}
                      disabled={saving || editing}
                      label={t("difficulty.set_all")}
                      className="max-md:[&>button]:h-11 max-md:[&>button]:min-w-[2.75rem]"
                    />
                    <span className="text-xs tabular-nums text-slate-500">
                      {t("difficulty.set_count", { count: difficultyCount, total: keptQuestions.length })}
                      {difficultyCount > 0 && (
                        <>
                          {" · "}
                          <span className="font-semibold text-emerald-700">
                            {t("difficulty.points", { count: difficultyPoints })}
                          </span>
                        </>
                      )}
                    </span>
                    {difficultyCount > 0 && (
                      <button
                        type="button"
                        onClick={() => pickDifficultyForAll(null)}
                        disabled={saving || editing}
                        className="rounded-lg px-2 py-1 text-xs font-medium text-slate-500 transition duration-200 enabled:hover:bg-slate-200/60 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 max-md:min-h-[2.75rem] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                      >
                        {t("difficulty.clear_all")}
                      </button>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500">
                    {needDifficulty ? t("my_questions.upload.incomplete.difficulty_hint") : t("difficulty.import_hint")}
                  </p>
                </div>
              )}
            </div>
          )}

          {/* What keeps the import from being saved, per field, with the questions to jump to. */}
          {blocked && saveCount > 0 && (
            <div
              className={`mb-3 flex gap-2 rounded-xl border px-3 py-2.5 text-sm ${
                anyRejected ? "border-red-200 bg-red-50 text-red-800" : "border-amber-200 bg-amber-50 text-amber-900"
              }`}
            >
              <ExclamationTriangleIcon
                className={`mt-0.5 h-4 w-4 shrink-0 ${anyRejected ? "text-red-500" : "text-amber-500"}`}
                aria-hidden="true"
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold">
                  {t("my_questions.upload.incomplete.count", { n: problems.length, total: keptQuestions.length })}
                </p>
                {strict && <p className="mt-0.5">{t("my_questions.upload.incomplete.title")}</p>}
                <ul className="mt-1.5 space-y-1">
                  {summaryFields.map(({ field, indexes }) => (
                    <li key={field} className="flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
                      <span className="font-medium">{capitalize(t(`question_fields.${field}`, { defaultValue: field }))}:</span>
                      {indexes.length === keptQuestions.length && indexes.length > 1 ? (
                        <span>{t("my_questions.upload.incomplete.all")}</span>
                      ) : (
                        // Inline, so the commas stay with their numbers when the line wraps.
                        <span className="min-w-0">
                          {indexes.slice(0, MAX_LISTED).map((index, i) => (
                            <React.Fragment key={index}>
                              {i > 0 && <span aria-hidden="true">, </span>}
                              <button
                                type="button"
                                onClick={() => jumpToQuestion(index)}
                                aria-label={t("my_questions.upload.incomplete.go_to", { index })}
                                className={jumpClass}
                              >
                                {index}
                              </button>
                            </React.Fragment>
                          ))}
                          {indexes.length > MAX_LISTED &&
                            ` ${t("my_questions.upload.incomplete.more", { n: indexes.length - MAX_LISTED })}`}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {strict && !blocked && saveCount > 0 && (
            <p className="mb-3 flex items-center gap-1.5 text-sm font-medium text-emerald-700">
              <CheckCircleIcon className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
              {t("my_questions.upload.incomplete.all_complete")}
            </p>
          )}

          {removedCount > 0 && (
            <p className="mb-3 text-sm font-medium text-slate-600">
              {t("my_questions.upload.removed_summary", { count: removedCount })}
            </p>
          )}
          {/* Into a subject the summary above says it all: an unmarked answer is not taken there. */}
          {!strict && notMarkedCount > 0 && (
            <div className="mb-3 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
              {t("my_questions.upload.not_marked_summary", { marked: notMarkedCount, total: saveCount })}
            </div>
          )}
          {!strict && noCorrectCount > 0 && (
            <div className="mb-3 flex gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
              {t("my_questions.upload.no_correct_summary", { count: noCorrectCount })}
            </div>
          )}
          {!strict && noOptionsCount > 0 && (
            <div className="mb-3 flex gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
              {t("my_questions.upload.no_options_summary", { count: noOptionsCount })}
            </div>
          )}

          <ol
            aria-busy={saving}
            className={`mb-4 divide-y divide-slate-100 overflow-y-auto rounded-xl border border-slate-200 bg-white p-2 sm:p-4 ${
              editing ? "max-h-[75vh]" : "max-h-[55vh]"
            }`}
          >
            {shownQuestions.map((question) => {
              const check = checks.get(question.index);
              return (
                <PreviewQuestion
                  key={question.index}
                  question={question}
                  images={preview.images}
                  editable={preview.acceptsEdits}
                  removable={preview.acceptsSkip}
                  removed={!!preview.removed[question.index]}
                  locked={saving || editing}
                  isEditing={editingIndex === question.index}
                  refByDataUri={refByDataUri}
                  onPickCorrect={pickCorrect}
                  onStartEdit={startEdit}
                  onRevert={revertEdit}
                  onApplyEdit={applyEdit}
                  onCancelEdit={cancelEdit}
                  onRemove={removeQuestion}
                  onRestore={restoreQuestion}
                  difficulty={preview.difficulty[question.index] || null}
                  onDifficulty={preview.acceptsDifficulty ? pickDifficulty : null}
                  domId={questionDomId(question.index)}
                  strict={strict}
                  missing={check ? check.missing.join(",") : ""}
                  rejected={!!check?.rejected}
                  catalog={catalog}
                  subtopic={preview.subtopics[question.index] || null}
                  generalSubtopic={preview.generalSubtopic}
                  subtopicRequired={needSubtopic}
                  subtopicInvalid={!!check?.invalid}
                  onSubtopic={preview.acceptsSubtopics && catalog ? pickSubtopic : null}
                  grade={grade}
                />
              );
            })}
          </ol>

          {errorAlert}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end">
            {/* A loading Button shows only its spinner, so the progress text goes next to it. */}
            <p role="status" className="text-sm text-slate-500 empty:hidden max-sm:text-center sm:mr-auto">
              {saving
                ? t("my_questions.upload.saving")
                : editing
                ? t("my_questions.upload.finish_editing")
                : blocked
                ? t("my_questions.upload.incomplete.status", { count: problems.length })
                : ""}
            </p>
            {/* One row on phones too: "Back" and the main button filling the rest. */}
            <div className="flex gap-2">
              <Button
                className="shrink-0 whitespace-nowrap max-sm:h-11"
                text={t("buttons.back")}
                color="secondary-outline"
                onClick={() => {
                  setStep("form");
                  setError("");
                }}
                disabled={saving || editing}
              />
              <Button
                className="whitespace-nowrap max-sm:h-11 max-sm:flex-1"
                text={t("my_questions.upload.save_all", { count: saveCount })}
                Icon={ArrowUpTrayIcon}
                color="primary"
                onClick={handleSave}
                loading={saving}
                disabled={saving || editing || !saveCount || blocked}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="w-full">
          <p className="text-sm text-slate-500">{t("my_questions.upload.read_instruction")}</p>

          {/* ASCII paths: a missing file falls back to index.html, which would download the app as "PDF". */}
          <div className="-ml-2 mb-4 mt-1 flex flex-wrap gap-x-2">
            <a href="/instruction_kz.pdf" download="Нұсқаулық_тест_толтыру_KZ.pdf" className={linkClass}>
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              {t("my_questions.upload.instruction_kz")}
            </a>
            <a href="/instruction_ru.pdf" download="Инструкция_заполнение_теста_RU.pdf" className={linkClass}>
              <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
              {t("my_questions.upload.instruction_ru")}
            </a>
          </div>

          <fieldset className="mb-6 border-t border-slate-100 pt-4" disabled={busy}>
            <legend className="sr-only">{t("my_questions.upload.fields_title")}</legend>
            <p className="text-sm font-medium text-slate-900">{t("my_questions.upload.fields_title")}</p>
            <p className="mb-3 mt-0.5 text-xs text-slate-500">
              {allowNoFolder || folder ? t("my_questions.upload.fields_hint") : t("my_questions.upload.fields_hint_folder")}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {folder && (
                <div className="min-w-0 sm:col-span-2">
                  <p className="mb-1 block text-sm font-medium text-slate-700">{t("folders.subject_folder")}</p>
                  <p className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm text-slate-700">
                    <FolderIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                    <span className="truncate">{folderLabel || folder.name}</span>
                  </p>
                  {fieldErrors.folder && (
                    <span className="mt-1 block text-xs font-semibold text-error">{fieldErrors.folder}</span>
                  )}
                </div>
              )}
              {showFolderPicker && (
                <Select
                  className={`min-w-0 ${showSubject ? "" : "sm:col-span-2"}`}
                  label={t("folders.subject_folder")}
                  placeholder={t("folders.subject_folder_placeholder")}
                  options={folderOptions}
                  value={fields.folder}
                  onChange={(o) => updateField("folder", o?.value || "")}
                  isSearchable={true}
                  isDisabled={busy}
                  errorMsg={fieldErrors.folder}
                />
              )}
              {!folder && !showFolderPicker && fieldErrors.folder && (
                <p className="text-xs font-semibold text-error sm:col-span-2">{fieldErrors.folder}</p>
              )}
              {showSubject && (
                <Select
                  className={`min-w-0 ${showFolderPicker ? "" : "sm:col-span-2"}`}
                  label={t("my_questions.upload.subject")}
                  options={toOptions(subjects)}
                  value={fields.subject}
                  onChange={(o) => updateField("subject", o?.value || "")}
                  isSearchable={true}
                  isDisabled={busy}
                  errorMsg={fieldErrors.subject}
                />
              )}
              {/* The grade the file is for: the preview offers that grade's topics. */}
              {subjectUuid && gradeOptions.length > 0 && (
                <div className="min-w-0">
                  <Select
                    inputId={gradeInputId}
                    label={t("my_questions.upload.grade")}
                    options={[{ value: "", label: t("my_questions.upload.grade_all") }, ...gradeOptions]}
                    value={fields.grade}
                    onChange={(o) => updateField("grade", o?.value || "")}
                    isDisabled={busy}
                  />
                  <p className="mt-1 text-xs text-slate-500">{t("my_questions.upload.grade_hint")}</p>
                </div>
              )}
            </div>
          </fieldset>

          <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl bg-slate-50 px-4 py-3 ring-1 ring-inset ring-slate-200">
            <DocumentTextIcon className="h-6 w-6 shrink-0 text-slate-400" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-slate-500">{t("my_questions.upload.file")}</p>
              <p className="truncate text-sm font-medium text-slate-900">
                {file ? file.name : t("my_questions.upload.no_file")}
              </p>
            </div>
            <Button
              className="max-sm:h-11 max-sm:w-full"
              text={file ? t("my_questions.upload.change_file") : t("my_questions.upload.choose_file")}
              Icon={ArrowUpTrayIcon}
              color="secondary-outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
            />
          </div>
          {/* A PDF has only text: formulas, tables and Word numbering do not come through. */}
          {file?.name.toLowerCase().endsWith(".pdf") && (
            <div role="note" className="-mt-2 mb-4 flex gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
              <div className="space-y-1">
                <p className="font-semibold">{t("my_questions.upload.pdf_formulas_title")}</p>
                <p>{t("my_questions.upload.pdf_formulas_text")}</p>
                <p className="text-xs text-amber-700">{t("my_questions.upload.pdf_hint")}</p>
              </div>
            </div>
          )}

          {errorAlert}

          <input type="file" ref={fileInputRef} accept=".docx,.pdf" onChange={handleFileChange} className="hidden" />
          <Button
            className="w-full max-sm:h-11"
            text={t("my_questions.upload.preview")}
            Icon={EyeIcon}
            onClick={handlePreview}
            loading={previewing}
            disabled={!file || busy}
            color="primary"
          />
          {previewing && (
            <p role="status" className="mt-3 text-center text-sm text-slate-500">
              {t("my_questions.upload.previewing")}
            </p>
          )}
        </div>
      )}
    </CustomDialog>
  );
}
